import React, { useCallback, useState } from 'react';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { queryCache, useMutation, useQuery } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { QueryKey } from 'api/queryKey';
import { useEnsureContext } from 'common/hooks';
import { useParams } from 'react-router';
import { BusinessPartnerDoctorForm } from 'pages/BusinessPartnerManagement/BusinessPartnerDoctorsManagement/BusinessPartnerDoctorPage/BusinessPartnerDoctorForm';
import { Skeleton, message } from 'antd';
import { FormikOnSubmit } from 'lib/formik.utils';
import { DoctorFormModel } from 'models/doctor';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export const BusinessPartnerDoctorPage = () => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { partnerDoctorId } = useParams();
  const intlMessage = useFormatMessage();
  const [checkedDenyAccess, setCheckedDenyAccess] = useState(false);
  const [shouldPutPermissions, setShouldPutPermissions] = useState(false);

  const removeSpacesFromForm = (formData: DoctorFormModel) => {
    formData.phoneNumber =
      formData.phoneNumber &&
      formData.phoneNumber.replace(/\s/g, '').replace(/^0+/, '');
    formData.prefixCode =
      formData.prefixCode && formData.prefixCode.replace(/\s/g, '');
    return formData;
  };

  const { isLoading, data } = useQuery(
    [QueryKey.BusinessPartnerDoctor, bpContext.id, partnerDoctorId],
    () => {
      return BusinessPartnersAPI.fetchDoctorById(bpContext.id, partnerDoctorId);
    }
  );

  const [mutate] = useMutation(BusinessPartnersAPI.modifyDoctorById);

  const [mutateuserPermissions] = useMutation(
    BusinessPartnersAPI.updateUserPermissions
  );

  const permissionItems = [
    {
      permission: 'DIAGNOSIS',
      scope: !checkedDenyAccess ? 'OWN' : 'PARTNER',
      enabled: true,
      read: true,
      create: true,
      update: true,
      delete: true,
    },
  ];

  const handleSubmit: FormikOnSubmit<DoctorFormModel> = useCallback(
    (formData) => {
      const formDataToSend =
        formData.phoneNumber !== null
          ? removeSpacesFromForm(formData)
          : formData;

      mutate(
        {
          businessPartnerId:
            formDataToSend.partnerDetails && formDataToSend.partnerDetails.id
              ? formDataToSend.partnerDetails.id
              : bpContext.id,
          dto: formDataToSend,
        },
        {
          onSuccess: () => {
            queryCache.invalidateQueries(QueryKey.BusinessPartnerDoctor).then();
            formData.userId &&
              shouldPutPermissions &&
              mutateuserPermissions({
                userId: formData.userId,
                items: permissionItems,
              });
            message.success(
              intlMessage(
                { id: 'entity.updated' },
                {
                  name: `${formData.firstName} ${formData.lastName}`,
                }
              )
            );
          },
        }
      ).then();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bpContext, mutate, queryCache, intlMessage]
  );

  return (
    <>
      {isLoading && <Skeleton />}
      {!isLoading && data && (
        <BusinessPartnerDoctorForm
          value={data}
          loading={isLoading}
          onSubmit={handleSubmit}
          checkedDenyAccess={checkedDenyAccess}
          setCheckedDenyAccess={setCheckedDenyAccess}
          setShouldPutPermissions={setShouldPutPermissions}
        />
      )}
    </>
  );
};
